import React, { createContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeProvider as StyledProvider } from 'styled-components';

import GlobalStyle from '../styles';

import { useMediaQuery, useNavigation } from '../hooks';
import { get } from '../utils/iterable/get';

import { THEME_NAME } from '../constants/theme';
import { VELA_THEME } from '../constants/user';
import { FEATURE } from '../constants/features';
import { PAGES } from '../constants/routes';

export const ThemeContext = createContext();

ThemeContext.displayName = 'ThemeContext';

export default function ThemeProvider({ children }) {
  const { PAGE } = useNavigation();
  const saved = useSelector(get(['user', VELA_THEME]));
  const redesignEnabled = useSelector(get(['user', 'features', FEATURE.REDESIGN]));
  const autoDetect = saved === THEME_NAME.AUTO;
  const queryMatches = useMediaQuery({ enabled: autoDetect, query: '(prefers-color-scheme: dark)' });
  const name = useMemo(
    function factory() {
      if (PAGE === PAGES.ADMIN) {
        return THEME_NAME.LIGHT;
      }

      if (autoDetect) {
        return queryMatches
          ? THEME_NAME.DARK
          : THEME_NAME.LIGHT;
      }

      switch (saved) {
        case THEME_NAME.DARK:
        case THEME_NAME.LIGHT: {
          return saved;
        }

        default: {
          return THEME_NAME.LIGHT;
        }
      }
    },
    [PAGE === PAGES.ADMIN, autoDetect, queryMatches, saved],
  );

  const value = useMemo(
    function factory() {
      return {
        autoDetect,
        name,
        redesign: redesignEnabled,
      };
    },
    [autoDetect, name, redesignEnabled],
  );

  return (
    <ThemeContext.Provider value={value}>
      <StyledProvider theme={value}>
        <GlobalStyle theme={value} />
        {children}
      </StyledProvider>
    </ThemeContext.Provider>
  );
}

ThemeProvider.propTypes = {
  children: PropTypes.node,
};
